import { useState, useEffect, useCallback, useRef } from 'react';
import {
  listAudioTracks,
  addAudioTrack,
  updateAudioTrack,
  deleteAudioTrack,
  generateVoiceover,
  TTS_VOICES,
  AUDIO_KIND_META,
  type ContentAudioTrack,
  type AudioKind,
  type ContentScene,
} from '@/lib/contentStudio';
import { uploadLibraryFile } from '@/hooks/useUpload';

interface Props {
  projectId: number;
  scenes: ContentScene[];
  language: string;
}

const ORDER: AudioKind[] = ['voiceover', 'music', 'sfx', 'ambience'];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Pistes audio du montage : voix off, musique, effets sonores et ambiance.
 * Chaque piste peut être rattachée à une scène précise et réglée en volume.
 */
export default function AudioPanel({ projectId, scenes, language }: Props) {
  const [tracks, setTracks] = useState<ContentAudioTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState<number | null>(null);
  const [voiceId, setVoiceId] = useState('eve');
  const [ttsBusy, setTtsBusy] = useState<number | 'all' | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [draft, setDraft] = useState<{ kind: AudioKind; name: string; url: string; sceneId: string; volume: number }>({
    kind: 'voiceover', name: '', url: '', sceneId: '', volume: 1,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setTracks(await listAudioTracks(projectId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, []);

  const resetDraft = () => setDraft({ kind: 'voiceover', name: '', url: '', sceneId: '', volume: 1 });

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      setError('Le fichier doit être un fichier audio (MP3, WAV, OGG, M4A).');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadLibraryFile(file, 'content-studio/audio');
      setDraft((d) => ({ ...d, url, name: d.name || file.name.replace(/\.[^.]+$/, '') }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Échec de l'envoi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleAdd = async () => {
    if (!draft.url.trim()) { setError('Importez un fichier audio ou collez un lien.'); return; }
    setAdding(true);
    setError(null);
    try {
      await addAudioTrack(projectId, {
        kind: draft.kind,
        name: draft.name.trim() || AUDIO_KIND_META[draft.kind].label,
        url: draft.url.trim(),
        volume: draft.volume,
        scene_id: draft.sceneId ? Number(draft.sceneId) : null,
      });
      resetDraft();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setAdding(false);
    }
  };

  const setVolume = async (t: ContentAudioTrack, v: number) => {
    setTracks((prev) => prev.map((x) => (x.id === t.id ? { ...x, volume: v } : x)));
    try { await updateAudioTrack(t.id, { volume: v }); } catch { /* silencieux */ }
  };

  const handleDelete = async (t: ContentAudioTrack) => {
    try {
      if (playing === t.id) { audioRef.current?.pause(); setPlaying(null); }
      await deleteAudioTrack(t.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const togglePlay = (t: ContentAudioTrack) => {
    if (!t.url) return;
    if (playing === t.id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.src = t.url;
      audioRef.current.volume = Math.min(1, Math.max(0, t.volume));
      audioRef.current.play().then(() => setPlaying(t.id)).catch(() => setError('Lecture impossible.'));
    }
  };

  const sceneLabel = (sceneId: number | null): string => {
    if (sceneId == null) return 'Projet entier';
    const s = scenes.find((x) => x.id === sceneId);
    return s ? `Scène ${pad(s.number)}` : 'Scène';
  };

  const generateVo = async (scene: ContentScene | null) => {
    setTtsBusy(scene ? scene.id : 'all');
    setError(null);
    try {
      const targets = scene ? [scene] : scenes;
      for (const s of targets) {
        const text = (s.voiceover || s.script || '').trim();
        if (!text) continue;
        // On remplace une éventuelle voix off précédente de cette scène.
        const previous = tracks.filter((t) => t.kind === 'voiceover' && t.scene_id === s.id);
        for (const p of previous) {
          // eslint-disable-next-line no-await-in-loop
          await deleteAudioTrack(p.id);
        }
        // eslint-disable-next-line no-await-in-loop
        const { url } = await generateVoiceover({ text, language, voiceId });
        // eslint-disable-next-line no-await-in-loop
        await addAudioTrack(projectId, {
          kind: 'voiceover',
          name: `Voix off · Scène ${pad(s.number)}`,
          url,
          scene_id: s.id,
          volume: 1,
        });
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La génération de voix off a échoué');
    } finally {
      setTtsBusy(null);
    }
  };

  const grouped = ORDER.map((k) => ({ kind: k, items: tracks.filter((t) => t.kind === k) }));

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-base font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-music-2-line text-foreground-500"></i>Audio &amp; voix off
          </h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-2xl">
            Ajoutez la voix off, la musique, les effets sonores et l&apos;ambiance. Chaque piste peut être rattachée à
            une scène ou couvrir tout le projet.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      {/* Bloc d'ajout */}
      <div className="mb-5 bg-background-50 border border-background-200/70 rounded-xl p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Type de piste</label>
            <div className="relative">
              <select
                value={draft.kind}
                onChange={(e) => setDraft((d) => ({ ...d, kind: e.target.value as AudioKind }))}
                className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                {ORDER.map((k) => <option key={k} value={k}>{AUDIO_KIND_META[k].label}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Nom</label>
            <input
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="Ex : Voix off principale"
              className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Scène</label>
            <div className="relative">
              <select
                value={draft.sceneId}
                onChange={(e) => setDraft((d) => ({ ...d, sceneId: e.target.value }))}
                className="w-full appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-900 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                <option value="">Projet entier</option>
                {scenes.map((s) => <option key={s.id} value={s.id}>Scène {pad(s.number)}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1.5">Fichier audio</label>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-dashed border-background-300/70 text-xs font-medium text-foreground-600 hover:border-accent-300 transition-colors cursor-pointer"
            >
              {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
              {draft.url ? 'Remplacer' : 'Importer'}
            </button>
            <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-3">
          <input
            value={draft.url}
            onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
            placeholder="… ou collez un lien audio (https://…)"
            className="flex-1 min-w-0 px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-800 focus:outline-none focus:border-accent-300"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={adding || !draft.url.trim()}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {adding ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
            Ajouter la piste
          </button>
        </div>
      </div>

      <audio ref={audioRef} className="hidden" />

      {/* Voix off IA (xAI TTS) */}
      <div className="mb-5 bg-background-50 border border-primary-200/60 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
              <i className="ri-mic-2-line text-primary-600"></i>Voix off IA (xAI)
            </h4>
            <p className="text-[11px] text-foreground-500 mt-0.5 max-w-xl">
              Génère une vraie narration pour chaque scène, mémorisée comme piste audio du projet.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={voiceId}
                onChange={(e) => setVoiceId(e.target.value)}
                className="appearance-none px-3 py-2 pr-8 bg-background-50 border border-background-200/70 rounded-md text-xs text-foreground-700 focus:outline-none focus:border-accent-300 cursor-pointer"
              >
                {TTS_VOICES.map((v) => <option key={v.id} value={v.id}>{v.label} — {v.hint}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400 text-xs pointer-events-none"></i>
            </div>
            <button
              type="button"
              onClick={() => generateVo(null)}
              disabled={ttsBusy !== null || scenes.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {ttsBusy === 'all' ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-sparkling-2-line"></i>}
              Générer toutes
            </button>
          </div>
        </div>
        <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto">
          {scenes.length === 0 ? (
            <p className="text-[11px] text-foreground-400">Aucune scène pour l&apos;instant.</p>
          ) : scenes.map((s) => (
            <div key={s.id} className="flex items-center gap-2 bg-background-100/70 rounded-lg px-3 py-1.5">
              <span className="text-[11px] font-bold text-foreground-500 w-16 flex-shrink-0">Scène {pad(s.number)}</span>
              <span className="text-[11px] text-foreground-500 flex-1 truncate">{s.voiceover || s.script || '—'}</span>
              <button
                type="button"
                onClick={() => generateVo(s)}
                disabled={ttsBusy !== null || !(s.voiceover || s.script || '').trim()}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-background-200/70 text-[11px] font-medium text-foreground-600 whitespace-nowrap hover:bg-background-200/70 disabled:opacity-40 transition-colors cursor-pointer"
              >
                {ttsBusy === s.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-mic-line"></i>}Générer
              </button>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-xl text-accent-500"></i>
        </div>
      ) : tracks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-background-50 border border-background-200/70 rounded-xl text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-music-2-line text-xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Aucune piste audio</p>
          <p className="text-xs text-foreground-500 mt-1 max-w-xs">Importez une voix off ou une musique pour habiller votre montage.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.filter((g) => g.items.length > 0).map((g) => {
            const meta = AUDIO_KIND_META[g.kind];
            return (
              <div key={g.kind}>
                <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-400 mb-2 flex items-center gap-2">
                  <i className={meta.icon}></i>{meta.label}
                  <span className="text-foreground-300">({g.items.length})</span>
                </h4>
                <div className="space-y-2">
                  {g.items.map((t) => (
                    <div key={t.id} className="flex flex-wrap items-center gap-2 sm:gap-3 bg-background-50 border border-background-200/70 rounded-lg px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => togglePlay(t)}
                        className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 transition-colors cursor-pointer ${playing === t.id ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
                        title={playing === t.id ? 'Pause' : 'Écouter'}
                      >
                        <i className={playing === t.id ? 'ri-pause-line' : 'ri-play-line'}></i>
                      </button>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground-800 truncate">{t.name || meta.label}</p>
                        <span className="text-[11px] text-foreground-400 inline-flex items-center gap-1">
                          <i className="ri-movie-line"></i>{sceneLabel(t.scene_id)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 w-32 sm:w-40">
                        <i className="ri-volume-down-line text-foreground-400 text-sm"></i>
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.05}
                          value={t.volume}
                          onChange={(e) => setVolume(t, Number(e.target.value))}
                          className="flex-1 accent-[oklch(var(--primary-500))] cursor-pointer"
                        />
                        <span className="text-[11px] text-foreground-500 w-8 text-right">{Math.round(t.volume * 100)}%</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(t)}
                        className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                        title="Supprimer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}